import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiRequest } from '../../lib/api';

interface UserSubscription {
  id: string;
  user_id: string;
  tier: string;
  scrapes_limit: number;
  scrapes_used: number;
  period_start: string;
  period_end: string;
  is_active: boolean;
  email: string;
}

interface Analytics {
  tierDistribution: Record<string, number>;
  last30Days: {
    totalRequests: number;
    blockedRequests: number;
    successRate: string;
  };
}

export function AdminDashboard() {
  const { user, loading: authLoading } = useAuth();
  const [users, setUsers] = useState<UserSubscription[]>([]);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [editTier, setEditTier] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setError('Authentication required');
      setLoading(false);
      return;
    }
    loadData();
  }, [user, authLoading]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [usersRes, analyticsRes] = await Promise.all([
        apiRequest<{ users: UserSubscription[] }>('/api/admin/users'),
        apiRequest<Analytics>('/api/admin/analytics'),
      ]);
      setUsers(usersRes.users);
      setAnalytics(analyticsRes);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTier = async (userId: string) => {
    try {
      await apiRequest(`/api/admin/users/${userId}/tier`, {
        method: 'PATCH',
        body: JSON.stringify({ tier: editTier }),
      });
      setEditingUser(null);
      setEditTier('');
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update tier');
    }
  };

  if (!user && !authLoading) {
    return (
      <div className="min-h-screen bg-surface-950 flex items-center justify-center">
        <div className="card p-8 text-center max-w-md">
          <h2 className="text-xl font-bold text-white mb-4">Authentication Required</h2>
          <p className="text-surface-400 mb-6">Please sign in to access the admin dashboard.</p>
          <Link to="/" className="btn-primary">Go to App</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-950">
      <div className="max-w-7xl mx-auto p-6 lg:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-surface-400 hover:text-white transition-colors mb-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to app
            </Link>
            <h1 className="text-3xl font-bold text-white">Admin Dashboard</h1>
          </div>
          <button
            onClick={loadData}
            className="btn-secondary text-sm"
            disabled={loading}
          >
            Refresh
          </button>
        </div>

        {error && (
          <div className="card p-4 mb-6 border border-red-500/30 bg-red-500/10">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Analytics Cards */}
            {analytics && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                <div className="card p-4">
                  <p className="text-surface-400 text-sm mb-1">Total Requests (30d)</p>
                  <p className="text-2xl font-bold text-white">{analytics.last30Days.totalRequests}</p>
                </div>
                <div className="card p-4">
                  <p className="text-surface-400 text-sm mb-1">Blocked Requests</p>
                  <p className="text-2xl font-bold text-amber-400">{analytics.last30Days.blockedRequests}</p>
                </div>
                <div className="card p-4">
                  <p className="text-surface-400 text-sm mb-1">Success Rate</p>
                  <p className="text-2xl font-bold text-accent-400">{analytics.last30Days.successRate}%</p>
                </div>
                <div className="card p-4">
                  <p className="text-surface-400 text-sm mb-1">Tier Distribution</p>
                  <div className="flex gap-2 flex-wrap mt-1">
                    {Object.entries(analytics.tierDistribution).map(([tier, count]) => (
                      <span key={tier} className="text-xs bg-surface-700 text-surface-300 px-2 py-1 rounded capitalize">
                        {tier}: {count}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Users Table */}
            <div className="card overflow-hidden">
              <div className="p-4 border-b border-surface-700">
                <h2 className="text-lg font-semibold text-white">Users ({users.length})</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-surface-700">
                      <th className="text-left text-surface-400 text-sm font-medium p-4">Email</th>
                      <th className="text-left text-surface-400 text-sm font-medium p-4">Tier</th>
                      <th className="text-left text-surface-400 text-sm font-medium p-4">Usage</th>
                      <th className="text-left text-surface-400 text-sm font-medium p-4">Period End</th>
                      <th className="text-left text-surface-400 text-sm font-medium p-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-b border-surface-800 hover:bg-surface-800/50">
                        <td className="p-4 text-surface-300 text-sm">{u.email}</td>
                        <td className="p-4">
                          <span className={`text-xs font-medium px-2 py-1 rounded capitalize ${
                            u.tier === 'unlimited'
                              ? 'bg-primary-500/20 text-primary-400'
                              : u.tier === 'pro'
                              ? 'bg-accent-500/20 text-accent-400'
                              : 'bg-surface-700 text-surface-300'
                          }`}>
                            {u.tier}
                          </span>
                        </td>
                        <td className="p-4 text-surface-300 text-sm">
                          {u.scrapes_used} / {u.scrapes_limit === -1 ? 'unlimited' : u.scrapes_limit}
                        </td>
                        <td className="p-4 text-surface-400 text-sm">
                          {new Date(u.period_end).toLocaleDateString()}
                        </td>
                        <td className="p-4">
                          {editingUser === u.user_id ? (
                            <div className="flex items-center gap-2">
                              <select
                                value={editTier}
                                onChange={(e) => setEditTier(e.target.value)}
                                className="bg-surface-700 text-white text-sm rounded px-2 py-1 border border-surface-600"
                              >
                                <option value="free">Free</option>
                                <option value="pro">Pro</option>
                                <option value="unlimited">Unlimited</option>
                              </select>
                              <button
                                onClick={() => handleUpdateTier(u.user_id)}
                                className="text-accent-400 hover:text-accent-300 text-sm"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => { setEditingUser(null); setEditTier(''); }}
                                className="text-surface-400 hover:text-surface-300 text-sm"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => { setEditingUser(u.user_id); setEditTier(u.tier); }}
                              className="text-primary-400 hover:text-primary-300 text-sm"
                            >
                              Change Tier
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {users.length === 0 && (
                      <tr>
                        <td colSpan={5} className="p-8 text-center text-surface-400">
                          No users found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
